import { FC, JSX, useContext, useEffect, useMemo, useState } from "react";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import { Accordion } from "@app/shared/Accordion";
import { Link } from "react-router-dom";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import {
  EntityTypeEnum,
  NetworkAssessmentAnalysisType,
  ProviderBadgeStyle,
} from "@app/projects/enumerators";
import { ExportType } from "@app/exports/enumerations";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { RegionGranularity } from "@app/region-sets";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome, QesIcons } from "@app/shared/icons/qesIcons";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import ZipProjectService from "@app/projects/zipProjectService";
import NextClosestProvider from "@app/projects/results/servicing-provider/nextClosestProvider";
import { getSearchParametersFromFilterSelections, printNumericString } from "@app/projects/zip-projects/utility";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import FlyoutLink from "@app/shared/Flyouts/FlyoutLink";
import { getEntityIconClasses, getClaimsVolumeDefinitions } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import ServicingProviderProfile from "@app/projects/servicing-provider/ServicingProviderProfile";
import { ACCESS_POINT_PROFILE_FLYOUT_TITLE } from "@app/projects/servicing-provider/servicingProviderProfileConstants";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import { getPrimaryAndSecondaryRunConfigurations } from "@app/projects/network-assessment/adequacy/utility";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { NcpEnum } from "@app/projects/servicing-provider/types";
import { formatDate } from "@app/shared/formatDateTime";
import formatPhoneNumber from "@app/shared/formatPhoneNumber";
import { handleDistanceClick } from "@app/shared/utility/handleDistanceClick";
import { wafflesKeyColumnName, SortDirections, ColumnConfig } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";

interface ZipNextClosestProvidersProps {
  exportEndpoint: string;
  projectUrl: string;
  regionSetId: number;
  specialtyGroupCode: string;
}

interface ZipNcpTableData {
  [wafflesKeyColumnName]: string;
  name: JSX.Element;
  providerType: JSX.Element;
  avgDist: string;
  avgTime: string;
  maxDist: string;
  maxTime: string;
  servicing: string;
  membershipCount: string;
  specialty: string;
  address: JSX.Element;
  availability: JSX.Element;
  nppesCertificationDate: string;
  nppesLastUpdatedDate: string;
  effectiveness: string;
  efficiency: string;
  mipsScore: string;
  commercialClaimsVolume: string;
  medicaidClaimsVolume: string;
  medicareClaimsVolume: string;
  vaClaimsVolume: string;
  tricareClaimsVolume: string;
  totalClaimsVolume: string;
  locationConfidenceScore: string;
}

const locationConfidenceTooltip = <>
  <div className="text-start">
    High: 85% or higher confidence practitioner IS at address <br />
    Medium: 51%-85% confidence practitioner IS at address <br />
    Low: 51%-85% confidence practitioner is NOT at address <br />
    Very Low: 85% or higher confidence practitioner is NOT at address
  </div>
</>;

const zipProjectService = new ZipProjectService();

export const ZipNextClosestProvidersPanel: FC<ZipNextClosestProvidersProps> = (props: ZipNextClosestProvidersProps) => {
  const projectContext = useContext(ZipProjectContext);
  const { state } = projectContext;
  const [tableData, setTableData] = useState<ZipNcpTableData[]>([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [providers, setProviders] = useState<NextClosestProvider[]>([]);
  const [accessStandardText, setAccessStandardText] = useState<string>("");

  useEffect(() => {
    if (!props.regionSetId || !props.specialtyGroupCode)
      return;

    zipProjectService.getZipNextClosestProviders(
      state.project.projectId,
      props.regionSetId,
      state.runConfiguration.projectRunConfigurationId,
      props.specialtyGroupCode,
      NetworkAssessmentAnalysisType.ServiceArea,
      false,
      NcpEnum.All,
      state.skip,
      state.take,
      !!state.sortOption.column ? String(state.sortOption.column) : "",
      state.sortOption.direction || SortDirections.Asc,
      "" //search value
    ).then((response) => {
      const newTableData = response.providers.map((p) => renderProviderRow(p as NextClosestProvider));
      setTableData(newTableData);
      setProviders(response.providers as NextClosestProvider[]);
      setAccessStandardText(response.accessStandardText);
    })
  }, [
    props.regionSetId,
    props.specialtyGroupCode,
    state.project.projectId,
    state.runConfiguration.projectRunConfigurationId,
    state.skip,
    state.take,
    state.sortOption
  ]);

  const selectedProviders = useMemo(() => {
    return providers.filter((p) => selectedProviderIds.includes(p.providerId.toString()));
  }, [selectedProviderIds]);

  const selectedMemberships = useMemo(() => {
    const selectedMemberships: number[] = [state.runConfiguration.memberSetId];
    if (state.isDualMembershipMode) {
      selectedMemberships.push(state.dualMembershipRunConfiguration.memberSetId);
    }
    return selectedMemberships;
  }, [state.runConfiguration, state.isDualMembershipMode]);

  const columnConfig: ColumnConfig = {
    address: {
      displayText: "Address",
      noSort: true,
      testId: "address",
    },
    availability: {
      displayText: "Availability",
      noSort: true,
      testId: "availability",
    },
    avgDist: {
      displayText: "Avg Dist",
      hidden: !(state.runConfiguration.showDistance && state.project.showAverageDistance),
      noSort: true,
      testId: "avg-dist",
      tooltip: "Average distance from provider to members without access",
    },
    avgTime: {
      displayText: "Avg Time",
      hidden: !(state.runConfiguration.showTime && state.project.showAverageTime),
      noSort: true,
      testId: "avg-time",
      tooltip: "Average time from provider to members without access",
    },
    commercialClaimsVolume: {
      displayText: "Commercial Claims Volume",
      hidden: !state.runConfiguration.showPciData,
      noSort: true,
      testId: "commercial-claims-volume",
      tooltip: getClaimsVolumeDefinitions(),
    },
    effectiveness: {
      displayText: "Effectiveness (Quality)",
      hidden: !state.runConfiguration.showPciData,
      noSort: true,
      testId: "effectiveness",
      tooltip: "Effectiveness is a measure of quality where 5 is the best score (higher quality) and 1 is the worst score (lower quality). Scores are relative to other providers in the same specialty and state and are derived from claims insight into 1) Mortality 2) Complications 3) Days in acute care",
    },
    efficiency: {
      displayText: "Efficiency (Cost)",
      hidden: !state.runConfiguration.showPciData,
      noSort: true,
      testId: "efficiency",
      tooltip: "Efficiency is a measure of physician cost vs. peers where 5 is the best score (lower cost) and 1 is the worst score (higher cost).  Efficiency measures are based on 1) cost per visit 2) cost per patient.  Standardized pricing methodology is used, meaning efficiency is a measure of comparative utilization intensity and not rate/price differences",
    },
    locationConfidenceScore: {
      displayText: "Location Confidence Score",
      hidden: !state.runConfiguration.showLocationConfidenceScores,
      noSort: true,
      testId: "location-confidence-score",
      tooltip: locationConfidenceTooltip,
    },
    maxDist: {
      displayText: "Max Dist",
      hidden: !(state.runConfiguration.showDistance && state.project.showMaximumDistance),
      noSort: true,
      testId: "max-dist",
      tooltip: "Maximum distance from provider to members without access",
    },
    maxTime: {
      displayText: "Max Time",
      hidden: !(state.runConfiguration.showTime && state.project.showMaximumTime),
      noSort: true,
      testId: "max-time",
      tooltip: "Maximum time from provider to members without access",
    },
    medicaidClaimsVolume: {
      displayText: "Medicaid Claims Volume",
      hidden: !state.runConfiguration.showPciData,
      noSort: true,
      testId: "medicaid-claims-volume",
      tooltip: getClaimsVolumeDefinitions(),
    },
    medicareClaimsVolume: {
      displayText: "Medicare Claims Volume",
      hidden: !state.runConfiguration.showPciData,
      noSort: true,
      testId: "medicare-claims-volume",
      tooltip: getClaimsVolumeDefinitions(),
    },
    membershipCount: {
      displayText: "Membership Count",
      noSort: true,
      testId: "membership-count",
      tooltip: "Closest provider for this many members without access",
    },
    mipsScore: {
      displayText: "Mips Score",
      hidden: !state.runConfiguration.showMipsScore,
      noSort: true,
      testId: "mips-score",
    },
    name: {
      displayText: "Name",
      noSort: true,
      testId: "name",
    },
    nppesCertificationDate: {
      displayText: "NPPES Certification Date",
      hidden: !state.runConfiguration.showNppesDates,
      noSort: true,
      testId: "nppes-certification-date",
    },
    nppesLastUpdatedDate: {
      displayText: "NPPES Last Updated Date",
      hidden: !state.runConfiguration.showNppesDates,
      noSort: true,
      testId: "nppes-last-updated-date",
    },
    providerType: {
      displayText: "Provider Type",
      noSort: true,
      testId: "provider-type",
    },
    servicing: {
      displayText: "Servicing",
      noSort: true,
      testId: "servicing",
      tooltip: "Provides access to other members",
    },
    specialty: {
      displayText: "Specialty (Name)",
      noSort: true,
      testId: "specialty",
    },
    totalClaimsVolume: {
      displayText: "Total Claims Volume",
      hidden: !state.runConfiguration.showPciData,
      noSort: true,
      testId: "total-claims-volume",
      tooltip: getClaimsVolumeDefinitions(),
    },
    tricareClaimsVolume: {
      displayText: "Tricare Claims Volume",
      hidden: !state.runConfiguration.showPciData,
      noSort: true,
      testId: "tricare-claims-volume",
      tooltip: getClaimsVolumeDefinitions(),
    },
    vaClaimsVolume: {
      displayText: "VA Claims Volume",
      hidden: !state.runConfiguration.showPciData,
      noSort: true,
      testId: "va-claims-volume",
      tooltip: getClaimsVolumeDefinitions(),
    },
  };

  const getProviderNetworkType = (networkType: string): ProviderNetworkTypeEnum => {
    switch (networkType) {
      case "unknown": return ProviderNetworkTypeEnum.Unknown;
      case "network": return ProviderNetworkTypeEnum.Network;
      case "prospect": return ProviderNetworkTypeEnum.Prospect;
      case "market": return ProviderNetworkTypeEnum.Market;
      case "netnew": return ProviderNetworkTypeEnum.NetNew;
      default: return ProviderNetworkTypeEnum.Unknown;
    }
  }

  const renderProviderRow = (provider: NextClosestProvider) => {
    if (!provider) {
      return null;
    }
    const checkIcon = <QesIcon isDecorative iconName={QesIcons.Check} className="mb-1" />;
    /* oxlint-disable sort-keys */
    return {
      [wafflesKeyColumnName]: provider.providerId.toString(),
      name: (
        <>
          <FlyoutLink
            classNames="p-0 qes-button-secondary"
            content={{
              body: (
                <ServicingProviderProfile
                  isTerminated={false}
                  nextClosestProvider={true}
                  provider={provider}
                  providerNetworkType={getProviderNetworkType(String(provider.providerNetworkType).toLowerCase())}
                  isZipProject={true}
                  project={state.project}
                  regionSetId={props.regionSetId}
                  // the flyout requires a ProjectRunConfigurationResult instead of ProjectRunConfiguration
                  // zip projects don't use a ProjectRunConfigurationResult
                  // so this adds the only property it actually needs from that superset
                  runConfiguration={{ ...state.runConfiguration, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea } as ProjectRunConfigurationResult}
                  specialtyGroupCode={props.specialtyGroupCode}
                />
              ),
              title: ACCESS_POINT_PROFILE_FLYOUT_TITLE,
            }}
            testId={`network-providers-panel__provider-profile-flyout--${provider.providerId}`}
          >
            <i className={getEntityIconClasses(provider.entityType, " supress-panel-styles")} style={{ paddingRight: "16px" }} />
            {provider.displayName}
          </FlyoutLink>
          {!!provider.npi && (
            <span className="text-muted ml15 d-block">NPI: {provider.npi}</span>
          )}
          <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
            {!!provider.tin && !!provider.tin && (
              <span className="text-muted ml15 d-block">TIN: {provider.tin}</span>
            )}
          </RoleRestricted>
          {!!provider.organizationName
            && provider.entityType === EntityTypeEnum.Individual && (
              <span className="text-muted ml15 d-block">{provider.organizationName}</span>
            )}
        </>
      ),
      providerType: <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} providerNetworkType={provider.providerNetworkType} />,
      avgDist: printNumericString(provider.averageDistance),
      avgTime: printNumericString(provider.averageTime),
      maxDist: printNumericString(provider.maxDist),
      maxTime: printNumericString(provider.maxTime),
      servicing: provider.isServicing,
      membershipCount: printNumericString(provider.memberCount),
      specialty: provider.specialty,
      address: (
        <>
          {!!provider.address ? <>{provider.address}<br /></> : null}
          {!!provider.address2 ? <>{provider.address2}<br /></> : null}
          {provider.city}
          {!!provider.city && (!!provider.state || !!provider.zip) ? ", " : null}
          {provider.state} {provider.zip}
          {!!provider.phone ? <br /> : null}
          {!!provider.phone ?
            <>
              <TooltipWrapper text="Phone" position="bottom">
                <QesIcon iconName={QesIcons.PhoneV1} description="Phone number" />
              </TooltipWrapper>
              &nbsp; {formatPhoneNumber(provider.phone)}
            </> :
            null
          }
          {!!provider.fax ? <br /> : null}
          {!!provider.fax ?
            <>
              <TooltipWrapper text="Fax" position="bottom">
                <QesIcon iconName={QesIcons.Printer} description="Fax number" />
              </TooltipWrapper>
              &nbsp; {formatPhoneNumber(provider.fax)}
            </> :
            null
          }
        </>
      ),
      availability: (
        <>
          {!!provider.deactivatedNPI && <><span>{checkIcon} Deactivated</span><br /></>}
          {!!provider.medicareOptOut && <><RoleRestricted allowedRoles={[Roles.MEDICARE_OPT_OUTS]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkIcon} Medicare Opt-Out</span></RoleRestricted><br /></>}
          {!!provider.oigExcluded && <><RoleRestricted allowedRoles={[Roles.OIG]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkIcon} OIG Excluded</span></RoleRestricted><br /></>}
          {!!provider.oigWaiver && <><RoleRestricted allowedRoles={[Roles.OIG]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkIcon} OIG Waiver</span></RoleRestricted><br /></>}
          {!!provider.precluded && <><RoleRestricted allowedRoles={[Roles.PRECLUDED_PROVIDER_USER]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkIcon} Medicare Precluded</span></RoleRestricted><br /></>}
          {!!provider.invalidNPI && <><span>{checkIcon} Invalid NPI</span><br /></>}
          {!!provider.missingNPI && <><span>{checkIcon} Missing NPI</span><br /></>}
        </>
      ),
      nppesCertificationDate: formatDate(provider.nppesCertificationDate),
      nppesLastUpdatedDate: formatDate(provider.nppesLastUpdatedDate),
      effectiveness: printNumericString(provider.effectivenessRank),
      efficiency: printNumericString(provider.efficiencyRank),
      mipsScore: printNumericString(provider.mipsScore),
      commercialClaimsVolume: provider.commercialClaimsVolumeDisplay,
      medicaidClaimsVolume: provider.medicaidClaimsVolumeDisplay,
      medicareClaimsVolume: provider.medicareClaimsVolumeDisplay,
      vaClaimsVolume: provider.vaClaimsVolumeDisplay,
      tricareClaimsVolume: provider.tricareClaimsVolumeDisplay,
      totalClaimsVolume: provider.totalClaimsVolumeDisplay,
      locationConfidenceScore: provider.locationConfidenceScoresDisplay,
    };
    /* oxlint-enable sort-keys */
  }

  const { primaryRunConfiguration, secondaryRunConfiguration } = getPrimaryAndSecondaryRunConfigurations(state.project.projectRunConfigurations, state.runConfiguration.projectRunConfigurationId);

  const bulkActionBar = <>
    <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
      to={{
        pathname: props.exportEndpoint,
        state: {
          activeModule: "Adequacy",
          activeNetworkType: ProviderNetworkTypeEnum.NextClosestProviders,
          adequacyFilters: state.adequacyFilters,
          availableExportTypes: [ExportType.ZipAnalysisAdequacyServicingProvider],
          disableRegionFilters: true,
          disableSpecialtyGroupFilters: true,
          exportNetworkProviderCount: 3,
          filters: [],
          granularity: RegionGranularity.Zip,
          initialNetworkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
          networkTypes: [],
          nextClosestProviders: true,
          perspective: state.perspective,
          projectResourceId: state.project.resource.resourceId,
          projectResourceName: state.project.resource.name,
          projectURL: props.projectUrl,
          providerIds: selectedProviderIds.map((id) => parseInt(id, 10)),
          regionSetIds: [props.regionSetId],
          showAppendNextClosestProviders: state.runConfiguration && state.runConfiguration.showNextClosestNetworkProviders || false,
          showAppendPatternOfCareProviders: false,
          specialtyGroupCodes: [props.specialtyGroupCode],

          // new reportcustomization
          /* oxlint-disable sort-keys */
          projectName: state.project.resource.name,
          isZipProject: true,
          defaultReportType: ReportType.ZipAnalysisAdequacyServicingProvider,
          defaultProviderSource: ProviderNetworkTypeEnum.NextClosestProviders,
          networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
          showClosestProviderToMemberReport: state.project.showClosestProviderReport,
          primaryMembership: { alias: primaryRunConfiguration.memberSetAlias || primaryRunConfiguration.memberSetName, id: primaryRunConfiguration.memberSetId },
          secondaryMembership: { alias: secondaryRunConfiguration?.memberSetAlias || secondaryRunConfiguration?.memberSetName, id: secondaryRunConfiguration?.memberSetId },
          selectedMemberships,
          selectedTimeslice: state.runConfiguration.projectRunConfigurationId, // runConfigurationId
          isCompareMode: state.isCompareMode,
          selectedComparedTimeslice: state.compareToRunConfiguration?.projectRunConfigurationId || state.dualMembershipRunConfiguration?.projectRunConfigurationId,
          isViewingOpportunity: state.adequacyFilters.opportunity,
          showAddedProviders: state.isCompareMode || state.isDualMembershipMode,
          showMatchedProviders: state.isCompareMode || state.isDualMembershipMode,
          showRemovedProviders: state.isCompareMode || state.isDualMembershipMode,
          showPatternOfCare: state.project.showPatternOfCareProviders,
          hasProspectProviders: !!state.runConfiguration.prospectProviderSetId,
          hasMarketProviders: !!state.runConfiguration.marketProviderSetId,
          showAdvancedComparison: state.project.showAdvancedComparison,
          zipProjectSearchParameters: getSearchParametersFromFilterSelections(state.filterSelections),
          showRegionSummaryReport: false,
          /* oxlint-enable sort-keys */
        },
      }}
      data-testid={"zip-next-closest-providers-panel__create-report-button"}
    >
      <QesIcon iconName={FontAwesome.CloudDownload} isDecorative className="align-baseline" /> Create Report
    </Link>
    <TooltipWrapper text="Measure distance between two selected providers" containerClassName="ms-3">
      <QESButton qesType="secondary" className="px-0" disabled={selectedProviderIds.length !== 2} onClick={() => handleDistanceClick(selectedProviders, "id")} qesTestId="zip-next-closest-providers-panel__distance-button">
        <QesIcon isDecorative iconName={QesIcons.Route} className="me-1" /> Distance
      </QESButton>
    </TooltipWrapper>
  </>;

  const accordionTitle = useMemo(() => {
    const base = "Next Closest Providers";
    if (!!projectContext.state.runConfiguration?.nextClosestProviderSetAlias) {
      return `${base} (${projectContext.state.runConfiguration.nextClosestProviderSetAlias})`;
    }

    return base;
  }, [projectContext.state.runConfiguration]);

  return (
    <Accordion
      testId="zip-next-closest-providers-panel__accordion"
      title={accordionTitle}
    >
      <p className="mb-3">The following are based on members without access. These Providers are outside access standard of {accessStandardText}.</p>
      <Waffles<ZipNcpTableData>
        bulkActionBar={bulkActionBar}
        columns={columnConfig}
        data={tableData}
        fullWidth={true}
        handleCheckboxChange={(newKeys: string[]) => setSelectedProviderIds(newKeys)}
        selectedKeys={selectedProviderIds}
        showCheckboxes={true}
        testId="zip-next-closest-providers-panel__table"
      />
    </Accordion>
  );
}